import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { getUnreadNotifications, markNotificationsRead } from '../utils/api';

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [count, setCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const dropRef = useRef(null);

  const token = localStorage.getItem('agentToken');
  const agentInfo = JSON.parse(localStorage.getItem('agentInfo') || '{}');
  const isAdmin = agentInfo.role === 'admin' || agentInfo.role === 'super_admin';

  useEffect(() => {
    if (!isAdmin || !token) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [isAdmin, token]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await getUnreadNotifications(token);
      setNotifications(res.notifications || []);
      setCount(res.count || 0);
    } catch (_) {}
  };

  const handleMarkAllRead = async () => {
    try {
      await markNotificationsRead([], token);
      setNotifications([]);
      setCount(0);
    } catch (_) {}
  };

  const handleClick = (notif) => {
    if (notif.link) navigate(notif.link);
    markNotificationsRead([notif.id], token);
    setNotifications(prev => prev.filter(n => n.id !== notif.id));
    setCount(prev => Math.max(0, prev - 1));
    setIsOpen(false);
  };

  if (!isAdmin) return null;

  return (
    <div className="relative" ref={dropRef} data-testid="notification-bell">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-500 hover:text-brand-blue rounded-md hover:bg-gray-100 transition"
        data-testid="bell-btn"
      >
        <Bell size={20} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="notification-count">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h4 className="font-bold text-sm text-gray-900">Notifications</h4>
            {count > 0 && (
              <button onClick={handleMarkAllRead} className="text-xs text-brand-blue hover:underline" data-testid="mark-all-read">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-400 text-sm">No new notifications</div>
            ) : (
              notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className="w-full text-left px-4 py-3 hover:bg-blue-50 transition border-b last:border-0"
                  data-testid={`notification-item-${n.id}`}
                >
                  <p className="text-sm font-semibold text-gray-900">{n.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{n.message}</p>
                  <p className="text-[10px] text-gray-300 mt-1">
                    {new Date(n.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
